// stores/panelDataStore.deepen.ts — interval re-deepen orchestration (Task 2).
//
// Extracted from panelDataStore.ts (to keep it < 250 LOC) following the same
// pattern as panelDataStore.older.ts: the single-flight state machine + transient
// abort-handle map live here; the store calls maybeDeepen from subscribe().
//
// PROBLEM: ensureLoaded is single-flight per SYMBOL, so the FIRST subscriber's
// interval fixes the fetch DEPTH. If a 1m chart (or a book/trades panel with no
// TF) seeds first, then the user raises the chart to 4h, the already-'ready' 1m
// base array is too shallow for the aggregated 4h view's long-MA / warmup
// indicators — and it stays shallow until a full reload.
//
// FIX: track the seedDepth used for the current load (_seedDepth). When a
// subscribe() requests a LARGER required base-candle count than what's loaded,
// fire a ONE-SHOT deeper re-fetch via loadSymbol1m at the deeper interval. The
// result MERGES (dedupeSorted via setCandles) — it extends the base array and
// never tears down live data. Guards:
//   • single-flight per symbol (_deepening) — re-entrant calls no-op,
//   • only fires on a STRICT depth increase (deeperBaseCandles returns null at
//     the SERVER_MAX_LIMIT ceiling → never loops at the cap),
//   • only when the symbol is genuinely 'ready' (a 'loading' seed already owns
//     the depth race; we re-check after it resolves on the next subscribe).

import { deeperBaseCandles, maxEnabledMaPeriod, requiredBaseCandles } from '../api/candleDepth';
import {
  loadSymbol1m,
  type SymbolLoadHandle,
} from '../api/panelDataFetcher';
import { setCachedSymbol } from '../api/candleCache';
import { omitKey } from './panelDataMutations';
import type { PanelDataState, SymbolKey } from './panelDataStore.types';

/** Transient abort handles for in-flight DEEPEN loads — NOT store state (side-effect
 *  bookkeeping only). Aborted in the store's clear(symbol). */
export const inflightDeepen = new Map<SymbolKey, SymbolLoadHandle>();

type Get = () => PanelDataState;
type Set = (
  partial:
    | Partial<PanelDataState>
    | ((s: PanelDataState) => Partial<PanelDataState>),
) => void;

/**
 * Background deep-history load for the two-phase first paint. After the FAST shallow seed has
 * painted the chart ('ready'), pull the full depth-policy history (indicator warmup + scroll-back)
 * to `targetDepth` 1m bars WITHOUT blocking the view, then merge it in. Single-flight per symbol
 * (reuses the _deepening guard + inflightDeepen map). No-op if a deepen is already running or the
 * symbol is already at/over the target. Unlike maybeDeepen this takes an explicit target depth
 * (driven by first-load, not an interval raise) and a fixed fetch limit.
 */
export function backgroundDeepen(
  get: Get,
  set: Set,
  symbol: SymbolKey,
  targetDepth: number,
): void {
  const s = get();
  if (s._deepening[symbol]) return; // single-flight
  const entry = s.dataByKey[symbol];
  if (!entry || entry.loadState !== 'ready') return;
  if ((s._seedDepth[symbol] ?? 0) >= targetDepth) return; // already deep enough

  set((st) => ({ _deepening: { ...st._deepening, [symbol]: true } }));
  // interval=1 with an explicit limit = targetDepth: fetch the full 1m history the depth policy
  // wants. The deeper series MERGES (dedupeSorted via setCandles) onto the painted fast seed.
  const handle = loadSymbol1m(symbol, 1, targetDepth);
  inflightDeepen.set(symbol, handle);
  void handle.promise
    .then(({ candles, flow }) => {
      if ((get().refcounts[symbol] ?? 0) === 0) return; // dropped mid-flight
      if (candles.length === 0) return;
      set((st) => ({ _seedDepth: { ...st._seedDepth, [symbol]: targetDepth } }));
      const existing = get().dataByKey[symbol];
      const merged = existing ? [...existing.candles, ...candles] : candles;
      const flowToUse = flow.length > 0 ? flow : (existing?.tradeFlow ?? flow);
      get().setCandles(symbol, merged, flowToUse);
      setCachedSymbol(symbol, {
        candles: get().dataByKey[symbol]?.candles ?? candles,
        flow: flowToUse,
        seedDepth: targetDepth,
      });
    })
    .catch(() => {
      // Teardown / transient REST failure — leave the fast seed in place; a later
      // subscribe or a scroll-back loadOlder fills history instead.
    })
    .finally(() => {
      inflightDeepen.delete(symbol);
      set((st) => ({ _deepening: omitKey(st._deepening, symbol) }));
    });
}

/**
 * One-shot deeper re-fetch when a newly-arriving subscriber's interval needs more
 * 1m base bars than the current load provided. No-op unless the symbol is 'ready'
 * with a known seedDepth, no deepen is already in flight, and the new interval
 * strictly increases the required depth (respecting the SERVER_MAX_LIMIT cap).
 */
export function maybeDeepen(
  get: Get,
  set: Set,
  symbol: SymbolKey,
  interval: number,
): void {
  const s = get();
  if (s._deepening[symbol]) return; // already deepening (single-flight)
  const entry = s.dataByKey[symbol];
  // Only deepen a settled series. While 'loading'/'awaiting-seed' the in-flight
  // seed owns the depth — but a later subscribe() may NEVER come for an
  // already-mounted panel, so record the requested depth for the seed's
  // completion handler to consume (the first-load stuck-shallow race).
  if (!entry || entry.loadState !== 'ready') {
    if (entry && (entry.loadState === 'loading' || entry.loadState === 'awaiting-seed')) {
      const wanted = requiredBaseCandles(interval, maxEnabledMaPeriod());
      set((st) => ({
        _pendingDeepen: {
          ...st._pendingDeepen,
          [symbol]: Math.max(st._pendingDeepen[symbol] ?? 0, wanted),
        },
      }));
    }
    return;
  }
  const loaded = s._seedDepth[symbol] ?? 0;
  const deeper = deeperBaseCandles(interval, loaded);
  if (deeper === null) return; // loaded depth already covers it (or at the cap)

  set((st) => ({ _deepening: { ...st._deepening, [symbol]: true } }));
  // loadSymbol1m derives candleLimit from requiredBaseCandles(interval, maxMa),
  // which equals `deeper` — pass the interval so the depth policy stays the one
  // source of truth (no second place computing the limit).
  const handle = loadSymbol1m(symbol, interval);
  inflightDeepen.set(symbol, handle);
  void handle.promise
    .then(({ candles, flow }) => {
      if ((get().refcounts[symbol] ?? 0) === 0) return; // symbol dropped mid-flight
      if (candles.length === 0) return; // nothing deeper came back — leave as-is
      // Record the new depth FIRST so a concurrent subscribe sees the deeper
      // floor and won't re-fire, then merge. setCandles dedupeSorts the deeper
      // REST series with the live tail (REST wins on overlap; the buffer/WS
      // re-applies any bar newer than the fetch) — live data is never torn down.
      set((st) => ({ _seedDepth: { ...st._seedDepth, [symbol]: deeper } }));
      const existing = get().dataByKey[symbol];
      const merged = existing ? [...existing.candles, ...candles] : candles;
      const flowToUse =
        flow.length > 0 ? flow : (existing?.tradeFlow ?? flow);
      get().setCandles(symbol, merged, flowToUse);
      setCachedSymbol(symbol, {
        candles: get().dataByKey[symbol]?.candles ?? candles,
        flow: flowToUse,
        seedDepth: deeper,
      });
    })
    .catch(() => {
      // LoadAbortedError (teardown) or a transient REST failure — don't latch
      // anything; a later subscribe at the same/deeper interval re-tries.
    })
    .finally(() => {
      inflightDeepen.delete(symbol);
      set((st) => ({ _deepening: omitKey(st._deepening, symbol) }));
    });
}
